import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Camera, Layers, Droplet, Frame } from "lucide-react";
import Seo from "../components/Seo";
import { ProductCard } from "../components/ProductCard";
import { PhotoGrid, thumb } from "../components/PhotoGrid";
import { Testimonials } from "../components/Testimonials";
import { api, sized, srcSet } from "../lib/api";
import { useContent } from "../context/ContentContext";

const CUSTOM_ICONS = [Camera, Layers, Droplet, Frame];

export default function Home() {
  const { p } = useContent();
  const c = p("home");
  const [featured, setFeatured] = useState([]);
  const [stories, setStories] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [routes, setRoutes] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data.filter((p) => p.kind === "etching").slice(0, 3)));
    api.get("/stories").then((r) => setStories(r.data.slice(0, 3)));
    api.get("/photos/routes").then((r) => setRoutes(r.data || [])).catch(() => {});
    api.get("/photos", { params: { featured: "true", limit: 12 } }).then((r) => {
      if (r.data.items?.length) setPhotos(r.data.items);
      else api.get("/photos", { params: { limit: 12 } }).then((r2) => setPhotos(r2.data.items || []));
    }).catch(() => {});
  }, []);

  const featuredRoutes = (() => {
    const chosen = routes.filter((r) => r.featured_home);
    return (chosen.length ? chosen : routes).slice(0, 4);
  })();
  const shownPhotos = photos.slice(0, 3);
  const HERO = c.hero.image;

  const jsonLd = [
    { "@context": "https://schema.org", "@type": "Organization", name: "CaminoSapiens", url: window.location.origin, logo: HERO, image: HERO, description: "A small fine-art studio making hand-pulled photogravure etchings inspired by long-distance walking." },
    { "@context": "https://schema.org", "@type": "WebSite", name: "CaminoSapiens", url: window.location.origin },
  ];

  return (
    <div data-testid="home-page">
      <Seo title="CaminoSapiens — Art of the Long Walk" description="Hand-pulled photogravure etchings inspired by the Camino de Santiago and long-distance walking. Turn your own photograph into an original etching." image={HERO} jsonLd={jsonLd} />

      {/* Hero */}
      <section className="relative h-[94vh] min-h-[640px] lg:h-screen w-full overflow-hidden" data-testid="hero">
        <img src={HERO} srcSet={srcSet(HERO, [640, 1024, 1600, 2000])} sizes="100vw" alt={c.hero.image_alt} className="absolute inset-0 w-full h-full object-cover object-[70%_40%] sm:object-[center_42%]" width="2000" height="2000" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/35 to-black/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent" />
        <div className="relative h-full max-w-7xl mx-auto px-5 sm:px-8 flex flex-col justify-end pb-20">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="overline text-white/80 mb-4">{c.hero.eyebrow}</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1 }}
            className="font-display text-white text-5xl sm:text-6xl lg:text-7xl max-w-3xl leading-[1.02]">
            {c.hero.heading}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.25 }}
            className="text-white/90 text-base sm:text-lg max-w-xl mt-6 leading-relaxed font-light">
            {c.hero.body}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.4 }} className="flex flex-col sm:flex-row gap-4 mt-9">
            <Link to={c.hero.cta_primary_link} data-testid="hero-cta-custom" className="inline-flex items-center justify-center gap-2 bg-[#F9F8F6] text-[#1C1B1A] px-7 py-4 text-sm tracking-wide hover:bg-[#B35D4B] hover:text-white transition-colors">
              {c.hero.cta_primary_label} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to={c.hero.cta_secondary_link} data-testid="hero-cta-etchings" className="inline-flex items-center justify-center gap-2 border border-white/70 text-white px-7 py-4 text-sm tracking-wide hover:bg-white hover:text-[#1C1B1A] transition-colors">
              {c.hero.cta_secondary_label}
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-24 text-center">
        <p className="overline mb-6">{c.intro.eyebrow}</p>
        <p className="font-display text-2xl sm:text-3xl leading-relaxed text-[#1C1B1A]">
          {c.intro.body}
        </p>
        <Link to={c.intro.link_link} className="inline-flex items-center gap-2 text-sm link-underline mt-8">{c.intro.link_label} <ArrowRight className="w-4 h-4" /></Link>
      </section>

      {/* Featured etchings */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16" data-testid="featured-section">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="overline mb-3">{c.featured.eyebrow}</p>
            <h2 className="font-display text-4xl">{c.featured.heading}</h2>
          </div>
          <Link to="/etchings" className="hidden sm:flex items-center gap-2 text-sm link-underline">{c.featured.view_all_label} <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {featured.map((p, i) => <ProductCard key={p.id} p={p} index={i} />)}
        </div>
      </section>

      {/* Custom etching feature */}
      <section className="bg-[#F0EFEA] my-24 border-y border-[#E4E1DB]" data-testid="custom-feature">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="overline mb-4">{c.custom.eyebrow}</p>
            <h2 className="font-display text-4xl sm:text-5xl leading-tight whitespace-pre-line">{c.custom.heading}</h2>
            <p className="text-[#4A4947] mt-6 leading-relaxed">
              {c.custom.body}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-10">
              {(c.custom.steps || []).map((s, i) => {
                const Icon = CUSTOM_ICONS[i] || Camera;
                return (
                  <div key={i} className="text-center" data-testid={`custom-step-${i}`}>
                    <div className="mx-auto w-12 h-12 border border-[#1C1B1A] flex items-center justify-center mb-3">
                      <Icon className="w-5 h-5" strokeWidth={1.4} />
                    </div>
                    <p className="text-xs tracking-wide text-[#4A4947]">{s.label}</p>
                  </div>
                );
              })}
            </div>
            <Link to={c.custom.cta_link} data-testid="custom-feature-cta" className="inline-flex items-center gap-2 bg-[#1C1B1A] text-[#F9F8F6] px-7 py-4 text-sm mt-10 hover:bg-[#B35D4B] transition-colors">
              {c.custom.cta_label} <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="#testimonials" data-testid="custom-proof-link" className="mt-6 inline-flex items-center gap-1.5 text-sm link-underline text-[#4A4947] hover:text-[#B35D4B] transition-colors">See where our etchings have found a home <ArrowRight className="w-3.5 h-3.5" /></a>
          </div>
          <div className="relative">
            <img src={sized(c.custom.image, 1200)} srcSet={srcSet(c.custom.image, [600, 900, 1400])} sizes="(min-width:1024px) 50vw, 100vw" alt={c.custom.image_alt} className="w-full h-[520px] object-cover border border-[#E4E1DB]" width="1200" height="1000" loading="lazy" />
          </div>
        </div>
      </section>

      {/* Where our etchings have found a home */}
      <Testimonials />

      {/* Process */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-8" data-testid="process-section">
        <div className="text-center mb-14">
          <p className="overline mb-3">{c.process.eyebrow}</p>
          <h2 className="font-display text-4xl">{c.process.heading}</h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {(c.process.cards || []).map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1 }}>
              <img src={sized(s.image, 800)} srcSet={srcSet(s.image, [400, 700, 900])} sizes="(min-width:768px) 33vw, 100vw" alt={s.label} className="w-full h-64 object-cover border border-[#E4E1DB]" width="900" height="700" loading="lazy" />
              <p className="overline mt-4">{s.label}</p>
              <p className="text-sm text-[#4A4947] mt-1">{s.caption}</p>
            </motion.div>
          ))}
        </div>
        <div className="text-center mt-12">
          <Link to={c.process.see_full_link} className="inline-flex items-center gap-2 text-sm link-underline">{c.process.see_full_label} <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>

      {/* Stories */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24" data-testid="stories-section">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="overline mb-3">{c.stories.eyebrow}</p>
            <h2 className="font-display text-4xl">{c.stories.heading}</h2>
          </div>
          <Link to="/stories" className="hidden sm:flex items-center gap-2 text-sm link-underline">{c.stories.view_all_label} <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {stories.map((s, i) => (
            <motion.div key={s.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.08 }}>
              <Link to={`/stories/${s.slug}`} className="group block" data-testid={`home-story-${s.slug}`}>
                <div className="overflow-hidden border border-[#E4E1DB]">
                  <img src={sized(s.hero_image, 900)} srcSet={srcSet(s.hero_image)} sizes="(min-width:768px) 33vw, 100vw" alt={s.hero_alt || s.title} className="w-full h-64 object-cover transition-transform duration-1000 group-hover:scale-105" width="900" height="600" loading="lazy" />
                </div>
                <p className="overline mt-4">{s.published_at}</p>
                <h3 className="font-display text-2xl mt-2 leading-tight">{s.title}</h3>
                <p className="text-sm text-[#4A4947] mt-2 line-clamp-2">{s.excerpt}</p>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Featured routes */}
      {featuredRoutes.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24" data-testid="home-routes">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="overline mb-3">{c.routes.eyebrow}</p>
              <h2 className="font-display text-4xl">{c.routes.heading}</h2>
            </div>
            <Link to="/photography/routes" className="hidden sm:flex items-center gap-2 text-sm link-underline">{c.routes.view_all_label} <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className={`grid grid-cols-2 gap-6 ${featuredRoutes.length >= 4 ? "lg:grid-cols-4" : featuredRoutes.length === 3 ? "lg:grid-cols-3" : "lg:grid-cols-2"}`}>
            {featuredRoutes.map((r, i) => (
              <motion.div key={r.slug} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.08 }}>
                <Link to={`/photography/routes/${r.slug}`} className="group block" data-testid={`home-route-${r.slug}`}>
                  <div className="aspect-[3/4] overflow-hidden border border-[#E4E1DB] bg-[#F0EFEA]">
                    {r.cover && <img src={thumb(r.cover, 800)} alt={r.label} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" loading="lazy" />}
                  </div>
                  <p className="font-display text-xl mt-3">{r.label}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Photography favorites */}
      {shownPhotos.length > 0 && (
        <section className="bg-[#F0EFEA] border-y border-[#E4E1DB] py-24" data-testid="home-photography">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <div className="flex items-end justify-between mb-12">
              <div>
                <p className="overline mb-3">{c.photography.eyebrow}</p>
                <h2 className="font-display text-4xl">{c.photography.heading}</h2>
              </div>
              <Link to="/photography" className="hidden sm:flex items-center gap-2 text-sm link-underline">{c.photography.view_all_label} <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {shownPhotos.map((p, i) => (
                <Link key={p.id} to="/photography" className="group block" data-testid={`home-photo-${i}`}>
                  <div className="overflow-hidden border border-[#E4E1DB]">
                    <img src={thumb(p.image, 800)} alt={p.alt || p.title}
                      className="w-full h-72 object-cover transition-transform duration-1000 group-hover:scale-105" width="900" height="700" loading="lazy" />
                  </div>
                  {p.location && <p className="overline mt-3">{p.location}</p>}
                  {p.title && <p className="font-display text-lg">{p.title}</p>}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
